'use client';

import { FeatherX } from '@subframe/core';
import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useId } from 'react';

import { Dialog } from './Dialog';

export type IconDialogTone = 'brand' | 'success' | 'warning' | 'error';

const TONE_CLASSES: Record<IconDialogTone, { circle: string; icon: string }> = {
  brand: { circle: 'bg-brand-100', icon: 'text-brand-700' },
  success: { circle: 'bg-success-100', icon: 'text-success-700' },
  warning: { circle: 'bg-warning-100', icon: 'text-warning-700' },
  error: { circle: 'bg-error-100', icon: 'text-error-700' },
};

export interface IconDialogProps {
  open: boolean;
  /** Cor do círculo do ícone; ignorado sem `icon`. */
  tone?: IconDialogTone;
  /** Sem ícone, o cabeçalho mostra só o título (modais de formulário). */
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  width?: string;
  onOpenChange: (aberto: boolean) => void;
  onFechar?: () => void;
  children?: ReactNode;
  footer?: ReactNode;
}

/**
 * Casca padrão de modal: cabeçalho (ícone opcional + `h2`), descrição, corpo rolável e rodapé.
 * O título e a descrição são ligados ao diálogo por `aria-labelledby`/`aria-describedby`
 * explícitos porque o `Dialog` do Subframe não expõe o `Dialog.Title` do Radix.
 */
export function IconDialog({
  open,
  tone = 'brand',
  icon,
  title,
  description,
  width = 'w-[440px]',
  onOpenChange,
  onFechar,
  children,
  footer,
}: IconDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const toneClasses = TONE_CLASSES[tone];

  return (
    <AnimatePresence>
      {open && (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 4 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <Dialog.Content
              aria-labelledby={titleId}
              aria-describedby={description ? descriptionId : undefined}
              className={`${width} max-w-[92vw] flex-col items-stretch gap-0 overflow-hidden rounded-[16px] border-0 p-0 shadow-2xl`}
            >
              <div className="flex items-start gap-3.5 px-6 pt-6 pb-2">
                {icon ? (
                  <div
                    className={`flex h-10 w-10 flex-none items-center justify-center rounded-full ${toneClasses.circle}`}
                  >
                    <span className={`flex h-[18px] w-[18px] items-center ${toneClasses.icon}`}>
                      {icon}
                    </span>
                  </div>
                ) : null}
                <div className="flex flex-1 flex-col gap-0.5 pt-0.5">
                  <h2 id={titleId} className="text-heading-3 font-heading-3 text-default-font">
                    {title}
                  </h2>
                </div>
                {onFechar && (
                  <button
                    type="button"
                    aria-label="Fechar"
                    onClick={onFechar}
                    className="flex h-7 w-7 flex-none cursor-pointer items-center justify-center rounded-[8px] text-neutral-400"
                  >
                    <FeatherX className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {description && (
                <div className="px-6 pb-5">
                  <span id={descriptionId} className="text-caption font-caption text-subtext-color">
                    {description}
                  </span>
                </div>
              )}

              {children ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
              ) : null}

              {footer && (
                <div className="flex items-center justify-end gap-2 px-6 pb-6">{footer}</div>
              )}
            </Dialog.Content>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
